// 合并语言和货币选择器的模态弹窗功能
// 使用主题内置的tingle.modal系统

class CombinedLocalizationModal extends HTMLElement {
  constructor() {
    super();
    this.modal = null;
    this.sectionId = this.dataset.sectionId || 'global';
    this.cachedElements = new Map(); // 缓存DOM元素
    this.init();
  }

  init() {
    const triggerButton = this.querySelector('.button-localization');
    if (triggerButton) {
      triggerButton.addEventListener('click', this.openModal.bind(this));
    }
  }

  openModal(e) {
    e.preventDefault();
    
    const modalContent = this.querySelector('.combined-localization-modal-content');
    if (!modalContent) {
      console.warn('Combined localization modal content not found');
      return;
    }

    // 创建模态弹窗
    this.modal = new tingle.modal({
      footer: false,
      stickyFooter: false,
      closeMethods: ['overlay', 'button', 'escape'],
      closeLabel: 'Close',
      cssClass: ['combined-localization-modal-popup'],
      onOpen: () => {
        document.documentElement.classList.add('open-popup');
        this.bindModalEvents();
      },
      onClose: () => {
        document.documentElement.classList.remove('open-popup');
      },
      beforeClose: () => {
        return true;
      },
    });

    // 设置弹窗内容
    this.modal.setContent(modalContent.innerHTML);
    this.modal.open();
  }

  bindModalEvents() {
    // 使用事件委托提高性能，无需延迟
    const modalElement = document.querySelector('.combined-localization-modal-popup');
    if (!modalElement) {
      console.warn('Modal element not found');
      return;
    }

    // 使用事件委托，只绑定一个监听器到容器
    modalElement.addEventListener('click', (e) => {
      const countryOption = e.target.closest('.country-option');
      if (countryOption) {
        e.preventDefault();
        e.stopPropagation();
        this.handleCountrySelection(countryOption);
      }

      // 处理清除搜索按钮点击
      const clearButton = e.target.closest('.clear-search');
      if (clearButton) {
        e.preventDefault();
        e.stopPropagation();
        this.clearSearch();
      }
    });

    // 绑定搜索输入事件
    this.setupSearchFunctionality(modalElement);

    console.log('Event delegation set up for country selection and search');
  }

  handleCountrySelection(clickedOption) {
    const countryCode = clickedOption.dataset.value;
    const languageCode = clickedOption.dataset.language;
    
    if (!countryCode) {
      console.warn('Country option missing data-value:', clickedOption);
      return;
    }

    console.log('🌍 Country selected:', {
      countryCode, 
      languageCode,
      countryName: clickedOption.querySelector('.country-name')?.textContent?.trim(),
      languageDisplay: clickedOption.querySelector('.country-language')?.textContent?.trim(),
      isHongKong: countryCode === 'HK',
      isMacao: countryCode === 'MO',
      debugInfo: `HK/MO语言映射: ${countryCode} -> ${languageCode}`
    });

    // 更新视觉状态
    this.updateActiveState(clickedOption, 'country');

    // 同时提交国家和语言
    this.submitCountryForm(countryCode, languageCode);
  }

  updateActiveState(clickedOption, type) {
    // 使用缓存的活动选项
    const previousActive = this.cachedElements.get('activeOption');
    if (previousActive) {
      previousActive.classList.remove('active');
    }

    // 设置新的活动状态并缓存
    clickedOption.classList.add('active');
    this.cachedElements.set('activeOption', clickedOption);
  }

  submitCountryForm(countryCode, languageCode) {
    // 使用缓存的表单元素
    let countryForm = this.cachedElements.get('countryForm');
    if (!countryForm) {
      const modalElement = document.querySelector('.combined-localization-modal-popup');
      countryForm = modalElement?.querySelector('.country-form form');
      if (countryForm) {
        this.cachedElements.set('countryForm', countryForm);
      }
    }
    
    if (countryForm) {
      console.log('📝 Form submission details:', {
        formAction: countryForm.action,
        formMethod: countryForm.method,
        countryCode,
        languageCode
      });

      // 使用缓存的输入字段
      let countryInput = this.cachedElements.get('countryInput');
      if (!countryInput) {
        countryInput = countryForm.querySelector('input[name="country_code"]');
        if (countryInput) {
          this.cachedElements.set('countryInput', countryInput);
        }
      }
      
      if (countryInput) {
        countryInput.value = countryCode;
        console.log('✅ Country input set to:', countryInput.value);
      }

      // 处理语言输入字段
      if (languageCode) {
        let languageInput = this.cachedElements.get('languageInput');
        if (!languageInput) {
          languageInput = countryForm.querySelector('input[name="language_code"]');
          if (!languageInput) {
            // 创建并缓存语言输入字段
            languageInput = document.createElement('input');
            languageInput.type = 'hidden';
            languageInput.name = 'language_code';
            countryForm.appendChild(languageInput);
            console.log('🆕 Created new language input field');
          }
          this.cachedElements.set('languageInput', languageInput);
        }
        languageInput.value = languageCode;
        console.log('✅ Language input set to:', languageInput.value);
      }

      // 显示所有表单数据
      const formData = new FormData(countryForm);
      console.log('📋 Final form data:');
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}: ${value}`);
      }

      // 提交表单
      console.log('🚀 Submitting form...');
      countryForm.submit();
    } else {
      console.warn('Country form not found in modal');
    }
  }

  setupSearchFunctionality(modalElement) {
    const searchInput = modalElement.querySelector('.country-search-input');
    const clearButton = modalElement.querySelector('.clear-search');
    
    if (!searchInput) return;

    // 搜索输入事件
    searchInput.addEventListener('input', (e) => {
      const query = e.target.value.trim();
      this.filterCountries(query);
      
      // 显示/隐藏清除按钮
      clearButton.style.display = query ? 'flex' : 'none';
    });

    // 回车键搜索
    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        // 如果只有一个可见结果，自动选择
        const visibleOptions = modalElement.querySelectorAll('.country-option:not([style*="display: none"])');
        if (visibleOptions.length === 1) {
          this.handleCountrySelection(visibleOptions[0]);
        }
      }
    });

    // 缓存搜索输入框
    this.cachedElements.set('searchInput', searchInput);
    this.cachedElements.set('clearButton', clearButton);
  }

    filterCountries(query) {
    const modalElement = document.querySelector('.combined-localization-modal-popup');
    if (!modalElement) {
      console.error('Modal element not found for filtering');
      return;
    }

    console.log('🔍 Starting filter with query:', JSON.stringify(query));

    // 如果查询为空，显示所有结果
    if (!query.trim()) {
      console.log('Empty query, showing all countries');
      this.showAllCountries(modalElement);
      return;
    }

    const regionGroups = modalElement.querySelectorAll('.region-group');
    let hasVisibleResults = false;
    let matchedCountries = [];

    regionGroups.forEach((regionGroup, regionIndex) => {
      const countryOptions = regionGroup.querySelectorAll('.country-option');
      let hasVisibleInRegion = false;
      const regionGrid = regionGroup.querySelector('.region-grid');

      countryOptions.forEach((option) => {
        // 获取文本内容
        const countryNameElement = option.querySelector('.country-name');
        const countryLanguageElement = option.querySelector('.country-language');
        
        if (!countryNameElement || !countryLanguageElement) {
          option.style.display = 'none';
          return;
        }

        const countryName = countryNameElement.textContent.trim();
        const countryLanguage = countryLanguageElement.textContent.trim();
        
        // 检查是否匹配
        const nameMatches = countryName.includes(query);
        const languageMatches = countryLanguage.includes(query);
        const shouldShow = nameMatches || languageMatches;

        if (shouldShow) {
          option.style.display = 'block';
          option.classList.remove('search-hidden');
          hasVisibleInRegion = true;
          hasVisibleResults = true;
          matchedCountries.push({
            name: countryName,
            language: countryLanguage,
            reason: nameMatches ? 'name' : 'language'
          });
        } else {
          // 完全隐藏不匹配的元素
          option.style.display = 'none';
          option.classList.add('search-hidden');
        }
      });

      // 显示/隐藏区域
      regionGroup.style.display = hasVisibleInRegion ? '' : 'none';
      
      // 动态调整网格布局确保紧凑显示
      if (regionGrid && hasVisibleInRegion) {
        setTimeout(() => {
          const visibleCount = regionGroup.querySelectorAll('.country-option:not(.search-hidden):not([style*="display: none"])').length;
          console.log(`Region ${regionIndex} has ${visibleCount} visible countries`);
          
          // 根据可见项目数量优化网格布局
          if (visibleCount === 1) {
            regionGrid.style.gridTemplateColumns = '1fr';
          } else if (visibleCount === 2) {
            regionGrid.style.gridTemplateColumns = 'repeat(2, 1fr)';
          } else {
            regionGrid.style.gridTemplateColumns = 'repeat(3, 1fr)';
          }
        }, 0);
      }
    });

    console.log(`🎯 Search "${query}" matched ${matchedCountries.length} countries:`, matchedCountries);

    // 显示/隐藏无结果消息
    this.showNoResultsMessage(!hasVisibleResults, query);
  }

  showAllCountries(modalElement) {
    // 显示所有国家和区域
    const regionGroups = modalElement.querySelectorAll('.region-group');
    regionGroups.forEach(regionGroup => {
      regionGroup.style.display = '';
      const regionGrid = regionGroup.querySelector('.region-grid');
      
      const countryOptions = regionGroup.querySelectorAll('.country-option');
      countryOptions.forEach(option => {
        option.style.display = '';
        option.classList.remove('search-hidden');
      });

      // 重置网格布局为默认的3列
      if (regionGrid) {
        regionGrid.style.gridTemplateColumns = 'repeat(3, 1fr)';
      }
    });

    // 隐藏无结果消息
    const noResultsDiv = modalElement.querySelector('.no-search-results');
    if (noResultsDiv) {
      noResultsDiv.style.display = 'none';
    }
  }

  showNoResultsMessage(show, query) {
    const modalElement = document.querySelector('.combined-localization-modal-popup');
    let noResultsDiv = modalElement.querySelector('.no-search-results');

    if (show) {
      if (!noResultsDiv) {
        noResultsDiv = document.createElement('div');
        noResultsDiv.className = 'no-search-results';
        noResultsDiv.innerHTML = `
          <div style="text-align: center; padding: 40px 20px; color: #666;">
            <svg style="width: 48px; height: 48px; margin-bottom: 16px; opacity: 0.5;" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd" />
            </svg>
            <div style="font-size: 16px; font-weight: 500; margin-bottom: 8px;">No countries found</div>
            <div style="font-size: 14px;">Try searching with different keywords</div>
          </div>
        `;
        modalElement.querySelector('.custom-popup-content').appendChild(noResultsDiv);
      }
      noResultsDiv.style.display = 'block';
    } else if (noResultsDiv) {
      noResultsDiv.style.display = 'none';
    }
  }

  clearSearch() {
    const searchInput = this.cachedElements.get('searchInput');
    const clearButton = this.cachedElements.get('clearButton');
    
    if (searchInput) {
      searchInput.value = '';
      searchInput.focus();
      this.filterCountries('');
    }
    
    if (clearButton) {
      clearButton.style.display = 'none';
    }
  }
}

// 注册自定义元素
if (!customElements.get('combined-localization-modal')) {
  customElements.define('combined-localization-modal', CombinedLocalizationModal);
} 